import { UpgradeOffer } from '@pubstack/common/src/upgrade'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent, ReactNode } from 'react'
import { WithClassName } from '~/types/utils'
import { UpgradeOfferCard } from './UpgradeOfferCard'

type Props = WithClassName & {
  offers: UpgradeOffer[]
  context?: ReactNode
  /**
   * What sits behind the blur. Defaults to a drawing of a table.
   *
   * Real page chrome is fine here — a caller may pass its whole `PageContent` so the blur runs to the top
   * of the page rather than stopping under the title. What must never be in it is a real product list: the
   * products behind these walls are the ones the scope has not bought, so mounting their lists would fire
   * queries the API answers with 403 — a screenshot of an error is not a better backdrop than a drawing,
   * and it would be a real one taken against someone else's data.
   */
  backdrop?: ReactNode
}

/**
 * A page — or a tab — that the account has not bought: what it would look like, out of focus, with the
 * offer over it.
 *
 * The pitch used to replace the page entirely, which left a publisher reading about a product with no
 * idea what it would give them to work with. Showing the shape of the thing behind the ask is the whole
 * design: it is a list of ad units, and it is one upgrade away.
 *
 * The backdrop is inert in every sense — `aria-hidden`, unfocusable, and not clickable — so nothing
 * behind the blur can be tabbed into or read out as if it were real content.
 */
export const UpgradeLockedPanel: FunctionComponent<Props> = ({ className, offers, context, backdrop }) => (
  <div className={cn('relative isolate min-h-[560px] w-full overflow-hidden', className)}>
    {/* In flow, not `absolute inset-0`: the backdrop is what gives the panel its height, so `min-h` reads as
        the floor it looks like rather than a ceiling. With both layers taken out of flow nothing could grow
        the panel — it was exactly 560px tall whatever it wrapped, and `overflow-hidden` then cut off the
        bottom of anything taller. A caller passing a whole page (see `AdUnitsTabsPage`) is well past 560. */}
    <div aria-hidden className={'pointer-events-none relative select-none blur-[4px]'} inert>
      {backdrop ?? <LockedTableDrawing />}
    </div>
    <div className={'absolute inset-0 flex items-start justify-center overflow-auto bg-white/50 px-4 py-10'}>
      {/* The same card the Catalog's modal shows, so one offer does not arrive as two designs — see
          `UpgradeOfferCard`, which owns the frame. */}
      <UpgradeOfferCard offers={offers} context={context} />
    </div>
  </div>
)

const ROW_WIDTHS = ['w-[42%]', 'w-[64%]', 'w-[38%]', 'w-[55%]', 'w-[47%]', 'w-[70%]', 'w-[35%]', 'w-[58%]', 'w-[44%]', 'w-[62%]']

/**
 * A list, drawn rather than fetched — the width of the rows varies so it reads as names rather than as
 * a loading state, which is the one thing it must not be mistaken for.
 *
 * Exported so a caller building its own `backdrop` (to blur more than this panel alone, e.g. a tab bar
 * sitting above it) can still fall back to the same drawing instead of inventing another one.
 */
export const LockedTableDrawing: FunctionComponent = () => (
  <div className={'flex flex-col gap-4 p-1'}>
    <div className={'flex items-center gap-3'}>
      <div className={'bg-platinum h-8 w-64 rounded'} />
      <div className={'bg-ghost h-8 w-32 rounded'} />
      <div className={'bg-king ml-auto h-8 w-28 rounded'} />
    </div>
    <div className={'border-platinum overflow-hidden rounded border bg-white'}>
      {ROW_WIDTHS.map((width, index) => (
        <div key={width + index} className={'border-platinum flex h-8 items-center gap-4 border-b px-3 last:border-b-0'}>
          <div className={'bg-platinum size-4 shrink-0 rounded'} />
          <div className={cn('bg-ghost h-3 rounded', width)} />
          <div className={'bg-ghost ml-auto h-3 w-16 rounded'} />
          <div className={'bg-ghost h-3 w-10 rounded'} />
        </div>
      ))}
    </div>
  </div>
)
